import React, { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { Modal, Fld, ConfirmButton } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { fmtMoney, todayStr } from "../lib/format.js";
import { rentalDays, autoPrice, lateInfo, estimateLateCharge, rentalTotal, useNow } from "../lib/rental-time.js";
import {
  useEquipmentForRental, useCustomersForRental, useSaveRental, useDeleteRental,
} from "./api.js";

const RENTAL_STATUS = { reserved: "Reserved", out: "On rent", returned: "Returned", cancelled: "Cancelled" };
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd;

export default function RentalForm({ rental, allRentals, onClose }) {
  const isNew = !rental?.id;
  const nowT = new Date().toTimeString().slice(0, 5);
  useNow(); // keeps the overdue banner counting while the form is open

  const [f, setF] = useState(() => ({
    equipmentId: rental?.equipment_id ?? "",
    customerId: rental?.customer_id ?? "",
    startDate: rental?.start_date ?? todayStr(),
    endDate: rental?.end_date ?? todayStr(),
    startTime: (rental?.start_time ?? "09:00").slice(0, 5),
    endTime: (rental?.end_time ?? "09:00").slice(0, 5),
    returnedDate: rental?.returned_date ?? "",
    returnedTime: (rental?.returned_time ?? "").slice(0, 5),
    total: rental?.rental_charge ?? "",
    deliveryFee: rental?.delivery_fee ?? 0,
    deposit: rental?.deposit ?? 0,
    damageWaiver: rental?.damage_waiver ?? 0,
    lateCharge: rental?.late_charge ?? "",
    fuelCharge: rental?.fuel_charge ?? "",
    damageCharge: rental?.damage_charge ?? "",
    otherFee: rental?.other_fee ?? "",
    otherFeeNote: rental?.other_fee_note ?? "",
    status: rental?.status ?? "reserved",
    paid: rental?.paid ?? false,
    notes: rental?.notes ?? "",
    manualPrice: rental?.manual_price ?? false,
  }));
  const [errs, setErrs] = useState({});
  const [formError, setFormError] = useState("");
  const { notify } = useToast();

  const equipment = useEquipmentForRental();
  const customers = useCustomersForRental();
  const save = useSaveRental();
  const del = useDeleteRental();
  const busy = save.isPending || del.isPending;

  const set = (k, v) => {
    setF((p) => {
      const next = { ...p, [k]: v };
      // Selecting "Returned" stamps the return now, as v10 did.
      if (k === "status" && v === "returned") {
        next.returnedDate = p.returnedDate || todayStr();
        next.returnedTime = p.returnedTime || nowT;
      }
      return next;
    });
    setErrs((p) => {
      const n = { ...p };
      if (k === "equipmentId") delete n.equipment;
      if (k === "customerId") delete n.customer;
      if (k === "startDate" || k === "endDate") delete n.dates;
      return n;
    });
  };

  const eq = (equipment.data ?? []).find((e) => e.id === f.equipmentId);
  const days = rentalDays({
    start_date: f.startDate, start_time: f.startTime,
    end_date: f.endDate, end_time: f.endTime,
  });
  const suggested = eq ? autoPrice(days, eq.day_rate, eq.week_rate, eq.month_rate) : 0;
  const rentalCharge = f.manualPrice ? Number(f.total) || 0 : suggested;

  const li = lateInfo({
    status: f.status,
    start_date: f.startDate, end_date: f.endDate, end_time: f.endTime,
    returned_date: f.returnedDate, returned_time: f.returnedTime,
  });
  const estLate = li && eq ? estimateLateCharge(eq.day_rate, li) : 0;

  const grandTotal = rentalTotal({
    rental_charge: rentalCharge,
    delivery_fee: f.deliveryFee,
    damage_waiver: f.damageWaiver,
    late_charge: f.lateCharge,
    fuel_charge: f.fuelCharge,
    damage_charge: f.damageCharge,
    other_fee: f.otherFee,
  });

  // Double-booking check: same machine, overlapping dates, still live.
  const conflict = f.equipmentId
    ? (allRentals ?? []).find(
        (r) =>
          r.id !== rental?.id &&
          r.equipment_id === f.equipmentId &&
          (r.status === "reserved" || r.status === "out") &&
          overlaps(f.startDate, f.endDate, r.start_date, r.end_date)
      )
    : null;

  async function onSave() {
    setFormError("");
    const e = {};
    if (!f.equipmentId) e.equipment = "Pick a machine";
    if (!f.customerId) e.customer = "Pick a customer";
    if (!f.startDate || !f.endDate) e.dates = "Both dates are required";
    else if (f.endDate < f.startDate) e.dates = "The return date is before the pickup date";
    if (Object.keys(e).length) { setErrs(e); return; }

    const num = (v) => Number(v) || 0;
    try {
      await save.mutateAsync({
        id: rental?.id,
        existingInvoiceNo: rental?.invoice_no,
        row: {
          equipment_id: f.equipmentId,
          customer_id: f.customerId,
          start_date: f.startDate,
          start_time: f.startTime || "09:00",
          end_date: f.endDate,
          end_time: f.endTime || "09:00",
          returned_date: f.status === "returned" ? (f.returnedDate || todayStr()) : null,
          returned_time: f.status === "returned" ? (f.returnedTime || nowT) : null,
          rental_charge: rentalCharge,
          delivery_fee: num(f.deliveryFee),
          deposit: num(f.deposit),
          damage_waiver: num(f.damageWaiver),
          late_charge: num(f.lateCharge),
          fuel_charge: num(f.fuelCharge),
          damage_charge: num(f.damageCharge),
          other_fee: num(f.otherFee),
          other_fee_note: f.otherFeeNote || null,
          total: grandTotal,
          manual_price: f.manualPrice,
          status: f.status,
          paid: f.paid,
          paid_date: f.paid ? (rental?.paid_date || todayStr()) : null,
          notes: f.notes || null,
        },
      });
      notify(isNew ? "Rental booked." : "Rental updated.");
      onClose();
    } catch (err) {
      setFormError(err.message);
    }
  }

  return (
    <Modal
      title={isNew ? "New rental" : `Edit rental${rental?.invoice_no ? ` · ${rental.invoice_no}` : ""}`}
      onClose={onClose}
      wide
      footer={
        <>
          {!isNew && (
            <ConfirmButton
              className="btn danger"
              disabled={busy}
              label={<><Trash2 size={15} /> Delete</>}
              confirmLabel="Click again to delete"
              onConfirm={async () => {
                setFormError("");
                try { await del.mutateAsync(rental.id); notify("Rental deleted."); onClose(); }
                catch (err) { setFormError(err.message); }
              }}
            />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : isNew ? "Book rental" : "Save changes"}
          </button>
        </>
      }
    >
      {formError && <div className="notice red">{formError}</div>}
      {conflict && (
        <div className="notice red">
          <b>Double booking.</b> This machine is already {RENTAL_STATUS[conflict.status].toLowerCase()} from{" "}
          {conflict.start_date} to {conflict.end_date}. Saving anyway is allowed — the calendar will show both.
        </div>
      )}

      <div className="frm">
        <Fld label="Equipment" err={errs.equipment}>
          <select value={f.equipmentId} onChange={(e) => set("equipmentId", e.target.value)}>
            <option value="">— Select machine —</option>
            {(equipment.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </Fld>
        <Fld
          label="Customer"
          err={errs.customer}
          hint={customers.data?.length === 0 ? "No customers yet — add one in the Customers tab first." : null}
        >
          <select value={f.customerId} onChange={(e) => set("customerId", e.target.value)}>
            <option value="">— Select customer —</option>
            {(customers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.business ? ` · ${c.business}` : ""}</option>
            ))}
          </select>
        </Fld>

        <Fld label="Pickup — date & time">
          <div style={{ display: "flex", gap: 8 }}>
            <input type="date" style={{ flex: 1.4 }} value={f.startDate} onChange={(e) => set("startDate", e.target.value)} />
            <input type="time" style={{ flex: 1 }} value={f.startTime} onChange={(e) => set("startTime", e.target.value)} />
          </div>
        </Fld>
        <Fld label="Due back — date & time" err={errs.dates}>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="date" style={{ flex: 1.4 }} value={f.endDate} onChange={(e) => set("endDate", e.target.value)} />
            <input type="time" style={{ flex: 1 }} value={f.endTime} onChange={(e) => set("endTime", e.target.value)} />
          </div>
        </Fld>

        <Fld
          label={`Rental charge (${days} day${days === 1 ? "" : "s"})`}
          hint={
            eq
              ? `Auto-priced from ${eq.name.split(" ")[0]} rates: ${fmtMoney(eq.day_rate)}/day · ${fmtMoney(eq.week_rate)}/wk · ${fmtMoney(eq.month_rate)}/mo`
              : "Pick a machine to auto-price"
          }
        >
          <input
            type="number"
            step="0.01"
            disabled={!f.manualPrice}
            value={f.manualPrice ? f.total : rentalCharge}
            onChange={(e) => set("total", e.target.value)}
          />
          <label className="checkline" style={{ marginTop: 6, fontSize: 12.5 }}>
            <input
              type="checkbox"
              checked={f.manualPrice}
              onChange={(e) => { set("manualPrice", e.target.checked); if (e.target.checked) set("total", rentalCharge); }}
            />
            Override price manually
          </label>
        </Fld>
        <Fld label="Delivery fee">
          <input type="number" step="0.01" value={f.deliveryFee} onChange={(e) => set("deliveryFee", e.target.value)} />
        </Fld>

        <Fld label="Damage waiver" hint="Optional protection charge — its own income line in Books.">
          <input type="number" step="0.01" value={f.damageWaiver} onChange={(e) => set("damageWaiver", e.target.value)} />
        </Fld>
        <Fld label="Security deposit (held)" hint="Refundable — not counted as income.">
          <input type="number" step="0.01" value={f.deposit} onChange={(e) => set("deposit", e.target.value)} />
        </Fld>

        <Fld label="Status">
          <select value={f.status} onChange={(e) => set("status", e.target.value)}>
            {Object.entries(RENTAL_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Fld>
        {f.status === "returned" && (
          <Fld label="Actually returned — date & time">
            <div style={{ display: "flex", gap: 8 }}>
              <input type="date" style={{ flex: 1.4 }} value={f.returnedDate} onChange={(e) => set("returnedDate", e.target.value)} />
              <input type="time" style={{ flex: 1 }} value={f.returnedTime} onChange={(e) => set("returnedTime", e.target.value)} />
            </div>
          </Fld>
        )}

        {li && (
          <div className="full" style={{ background: "#f9e5e2", border: "1px solid #e5c4bf", borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontWeight: 700, color: "var(--red)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={16} />
              {f.status === "returned" ? "Returned late by " : "Currently overdue by "}{li.label}
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <Fld
                label="Late charge ($)"
                hint={`Estimated ${fmtMoney(estLate)} — day rate per full late day + hourly proration. Edit or confirm.`}
              >
                <input
                  type="number"
                  step="0.01"
                  style={{ maxWidth: 160 }}
                  value={f.lateCharge}
                  onChange={(e) => set("lateCharge", e.target.value)}
                  placeholder={String(estLate)}
                />
              </Fld>
              <button type="button" className="btn dark sm" style={{ marginBottom: 22 }} onClick={() => set("lateCharge", estLate)}>
                Use estimate ({fmtMoney(estLate)})
              </button>
            </div>
          </div>
        )}

        {(f.status === "returned" || f.status === "out") && (
          <div className="full" style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Final charges{" "}
              <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 12.5 }}>
                — added to the final invoice at check-in
              </span>
            </div>
            <div className="frm" style={{ gap: "10px 14px" }}>
              <Fld label="Fuel charge ($)">
                <input type="number" step="0.01" value={f.fuelCharge} onChange={(e) => set("fuelCharge", e.target.value)} />
              </Fld>
              <Fld label="Damage charge ($)">
                <input type="number" step="0.01" value={f.damageCharge} onChange={(e) => set("damageCharge", e.target.value)} />
              </Fld>
              <Fld label="Other fee ($)">
                <input type="number" step="0.01" value={f.otherFee} onChange={(e) => set("otherFee", e.target.value)} />
              </Fld>
              <Fld label="Other fee description">
                <input value={f.otherFeeNote} onChange={(e) => set("otherFeeNote", e.target.value)} placeholder="e.g. Cleaning, missing keys" />
              </Fld>
            </div>
          </div>
        )}

        <Fld full label="Notes">
          <textarea
            value={f.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Jobsite address, attachments included, fuel policy, PO number…"
          />
        </Fld>

        <div
          className="full"
          style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", background: "#faf9f6", border: "1px solid var(--line)", borderRadius: 8, padding: "12px 14px" }}
        >
          <label className="checkline">
            <input type="checkbox" checked={f.paid} onChange={(e) => set("paid", e.target.checked)} /> Payment received
          </label>
          <div style={{ marginLeft: "auto", fontFamily: "var(--disp)", fontSize: 24, fontWeight: 700 }}>
            Total {fmtMoney(grandTotal)}
          </div>
        </div>
      </div>
    </Modal>
  );
}
